import {
  ROLE_NUMBER_ADMINISTRATOR, ROLE_NUMBER_CLINICAL, ROLE_NUMBER_CLINICAL_CI, ROLE_NUMBER_CLINICAL_MANAGER,
  ROLE_NUMBER_GLOBAL_ADMIN, ROLE_NUMBER_MDS_NURSE, ROLE_NUMBER_MDS_REGIONAL, ROLE_NUMBER_SOCIAL_WORKER,
  ROLE_NUMBER_TRIPLE_CHECK,
} from 'list-types-public';
import { titlesForRole } from './presets';
import { CENSUS_TRENDS_PATH, MONTHLY_CENSUS_PATH, TITLES, pathOf, targetForTitle } from './routes';
import { NavCtx, NavScreen } from './types';

const NON_LANDING = new Set<string>([TITLES.logOut, TITLES.profile]);
export const DEFAULT_MARKER = ' (default)';

/**
 * Role -> its post-login landing page. Single source of truth: the auth service redirects
 * through this table and the profile dropdown marks "(default)" from it.
 */
export function defaultPathForRole(roleValue: number): string {
  const role = Number(roleValue);
  if (role === 801) { return '/manage/admin'; }
  if (role === ROLE_NUMBER_GLOBAL_ADMIN) { return '/manageb/global-portal'; }
  if (role > 400 && role <= 500) { return '/manage/pricing'; }
  if (role === ROLE_NUMBER_TRIPLE_CHECK) { return '/manageb/triple-check-report'; }
  if ([
    ROLE_NUMBER_SOCIAL_WORKER, ROLE_NUMBER_MDS_REGIONAL, ROLE_NUMBER_MDS_NURSE, ROLE_NUMBER_CLINICAL_CI,
    ROLE_NUMBER_CLINICAL, ROLE_NUMBER_CLINICAL_MANAGER, ROLE_NUMBER_ADMINISTRATOR,
  ].includes(role)) { return '/manageb/bed-boards'; }
  // 'client/upload' (>700, 100), 'underwriting' (500-700) and 'manage/upload' (30-32) are not
  // registered routes — those roles had no working landing page. They all carry Referral Inbox
  // in their menu, so they fall through to it like everyone else.
  return '/manageb/dashboard';
}

/** Flat ordered screen list for a role — legacy `getScreensForRole` shape and order. */
export function screensForRole(role: number, ctx: NavCtx): NavScreen[] {
  return titlesForRole(role, ctx).map((title) => {
    const target = targetForTitle(title, role);
    const screen: NavScreen = { title, path: pathOf(target) };
    if (target.state) { screen.state = target.state; }
    return screen;
  });
}

export function buildLandingScreens(role: number, ctx: NavCtx): NavScreen[] {
  const screens = screensForRole(role, ctx).filter((s) => !!s.path && !NON_LANDING.has(s.title));

  // Census Trends links straight to the yearly census child; expose its Monthly sibling too.
  const trendsIdx = screens.findIndex((s) => s.path === CENSUS_TRENDS_PATH);
  const hasMonthly = screens.some((s) => s.path === MONTHLY_CENSUS_PATH);
  if (trendsIdx !== -1 && !hasMonthly) {
    screens.splice(trendsIdx + 1, 0, { title: TITLES.dailyTrendReport, path: MONTHLY_CENSUS_PATH });
  }

  const defaultPath = defaultPathForRole(role);
  const defaultIdx = screens.findIndex((s) => s.path === defaultPath);
  if (defaultIdx !== -1) {
    const [def] = screens.splice(defaultIdx, 1);
    screens.unshift({ ...def, title: def.title + DEFAULT_MARKER });
  }
  return screens;
}

export function isLandingScreenAllowed(role: number, path: string, ctx: NavCtx): boolean {
  return buildLandingScreens(role, ctx).some((s) => s.path === path);
}
